import React, { useState, useEffect, useRef } from 'react'
import { REVIEWS_DATA } from '../data/siteData'
import { useCMSCollection } from '../hooks/useCMS'

export function TestimonialsSlider() {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const touchStartX = useRef(0)
  const touchEndX = useRef(0)

  const { data: cmsReviews } = useCMSCollection('anmeldelser', REVIEWS_DATA)
  const items = Array.isArray(cmsReviews) && cmsReviews.length > 0 ? cmsReviews : REVIEWS_DATA

  // Determine items visible per screen size
  const [visibleCount, setVisibleCount] = useState(3)

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setVisibleCount(1)
      } else if (window.innerWidth < 1024) {
        setVisibleCount(2)
      } else {
        setVisibleCount(3)
      }
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const maxIndex = Math.max(0, items.length - visibleCount)

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1))
  }

  const handleNext = () => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1))
  }

  // Auto-advance slider
  useEffect(() => {
    if (isPaused) return
    const timer = setInterval(() => {
      handleNext()
    }, 6000)
    return () => clearInterval(timer)
  }, [isPaused, maxIndex])

  // Touch handlers
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX
  }

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX
  }

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        handleNext()
      } else {
        handlePrev()
      }
    }
  }

  return (
    <section className="py-20 bg-[#F4EFEB] overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
          <div>
            <span className="text-xs font-semibold tracking-wider text-[#7c875a] uppercase block mb-1">
              Ægte Klientfortællinger
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1b3a4b]">
              Hvad klienter oplever i forløb
            </h2>
            <p className="mt-2 text-sm sm:text-base text-[#64748B] max-w-xl">
              Autentiske tilbagemeldinger fra klienter, der har mærket kropsterapi, coaching og hypnoterapi indefra.
            </p>
          </div>

          {/* Navigation Arrows */}
          <div className="flex items-center gap-3 mt-6 md:mt-0">
            <button
              type="button"
              onClick={handlePrev}
              className="w-11 h-11 rounded-full bg-white border border-[#E5E0D8] text-[#1b3a4b] hover:bg-[#1b3a4b] hover:text-white transition-colors flex items-center justify-center shadow-sm cursor-pointer"
              aria-label="Forrige anmeldelse"
            >
              <i className="fa-solid fa-arrow-left text-sm" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={handleNext}
              className="w-11 h-11 rounded-full bg-white border border-[#E5E0D8] text-[#1b3a4b] hover:bg-[#1b3a4b] hover:text-white transition-colors flex items-center justify-center shadow-sm cursor-pointer"
              aria-label="Næste anmeldelse"
            >
              <i className="fa-solid fa-arrow-right text-sm" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Slider track */}
        <div
          className="relative select-none"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-out gap-6"
              style={{
                transform: `translateX(-${currentIndex * (100 / visibleCount + (24 / window.innerWidth) * 100)}%)`,
              }}
            >
              {items.map((item, idx) => (
                <div
                  key={idx}
                  className="flex-shrink-0"
                  style={{
                    width: `calc(${100 / visibleCount}% - ${((visibleCount - 1) * 24) / visibleCount}px)`,
                  }}
                >
                  <div className="h-full bg-white rounded-2xl p-7 sm:p-8 shadow-sm border border-[#E5E0D8]/80 flex flex-col justify-between hover:border-[#7c875a]/50 transition-colors">
                    <div>
                      {/* Quote mark */}
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-[#7c875a] text-3xl font-serif leading-none">“</span>
                        <div className="flex text-[#7c875a] text-xs gap-1">
                          <i className="fa-solid fa-star" />
                          <i className="fa-solid fa-star" />
                          <i className="fa-solid fa-star" />
                          <i className="fa-solid fa-star" />
                          <i className="fa-solid fa-star" />
                        </div>
                      </div>

                      {/* Quote text */}
                      <p className="text-sm sm:text-base text-[#334155] leading-relaxed italic line-clamp-6">
                        "{item.quote}"
                      </p>
                    </div>

                    {/* Author footer */}
                    <div className="mt-6 pt-5 border-t border-[#F1EBE4] flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#1b3a4b]/10 text-[#1b3a4b] font-serif font-bold text-sm flex items-center justify-center flex-shrink-0">
                        {item.author.charAt(0)}
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm text-[#1b3a4b] leading-tight">
                          {item.author}
                        </h4>
                        {item.note && (
                          <span className="text-xs text-[#64748B] block mt-0.5">
                            {item.note}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Indicators */}
        <div className="flex justify-center items-center gap-2 mt-8">
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setCurrentIndex(i)}
              className={`h-2 transition-all rounded-full cursor-pointer ${
                currentIndex === i ? 'w-8 bg-[#1b3a4b]' : 'w-2 bg-[#CBD5E1] hover:bg-[#94A3B8]'
              }`}
              aria-label={`Gå til dias ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  )
}
